import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { submitApplication } from '../../api/services';
import { useNotifications } from '../../store/AppContext';
import { useI18n } from '../../i18n';

interface ApplyToListingModalProps {
  open: boolean;
  onClose: () => void;
  listingDocumentId: string;
  listingTitle: string;
}

export function ApplyToListingModal({
  open,
  onClose,
  listingDocumentId,
  listingTitle,
}: ApplyToListingModalProps) {
  const { t } = useI18n();
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showNotification } = useNotifications();

  const handleSubmit = async () => {
    if (!content.trim()) {
      showNotification(t('listings.messageRequired'), 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await submitApplication({
        content,
        listing: listingDocumentId,
        // pilot_b2b will be populated by the backend based on the authenticated user
      });
      showNotification(t('listings.applicationSent'), 'success');
      onClose();
      setContent('');
    } catch (error: any) {
      showNotification(
        error.message || t('listings.applicationError'),
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>{t('listings.applyTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('listings.applyTo', { title: listingTitle })}. {t('listings.applicationMessageHint')}
        </Typography>
        <TextField
          autoFocus
          margin="dense"
          label={t('listings.messageLabel')}
          type="text"
          fullWidth
          multiline
          rows={6}
          variant="outlined"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          disabled={isSubmitting}
          placeholder={t('listings.messagePlaceholder')}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit" disabled={isSubmitting}>{t('listings.cancel')}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? t('listings.sending') : t('listings.sendApplication')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
